const CHANNEL_NAME = 'edudo-admin-poll';

interface SharedResult<T> {
  key: string;
  data: T;
  fetchedAt: number;
}

export interface PollCoordinator<T> {
  // Calls onLead once this tab may poll; the returned function gives the turn back.
  lead(onLead: () => void): () => void;
  share(data: T, fetchedAt: number): void;
  close(): void;
}

// Leading takes both: with locks but no channel, the tabs that wait for the lock would never get the
// leader's results.
const canCoordinate = () => !!navigator.locks && typeof BroadcastChannel !== 'undefined';

export const leadAlone = (onLead: () => void) => {
  onLead();
  return () => {};
};

// Tabs that poll the same key take turns through a lock, and the one holding it hands every result to
// the rest. Without support each tab polls on its own, which costs little: hidden tabs don't poll.
export function createPollCoordinator<T>(
  key: string,
  onShared: (data: T, fetchedAt: number) => void
): PollCoordinator<T> {
  if (!canCoordinate()) {
    return { lead: leadAlone, share: () => {}, close: () => {} };
  }

  const channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = ({ data: message }: MessageEvent<SharedResult<T>>) => {
    if (message?.key === key) onShared(message.data, message.fetchedAt);
  };

  return {
    lead(onLead) {
      let released = false;
      let releaseLock: () => void;
      const held = new Promise<void>(resolve => releaseLock = resolve);
      const controller = new AbortController();

      // A page without a normal origin (sandboxed iframe, file://) has navigator.locks but cannot take
      // a lock: request() rejects with SecurityError, as in http-service's withRefreshLock.
      navigator.locks.request<void>(`${CHANNEL_NAME}:${key}`, { signal: controller.signal }, () => {
        if (released) return;
        onLead();
        return held;
      }).catch(error => {
        if (error?.name === 'SecurityError' && !released) onLead();
      });

      return () => {
        released = true;
        controller.abort();
        releaseLock();
      };
    },
    share(data, fetchedAt) {
      channel.postMessage({ key, data, fetchedAt } as SharedResult<T>);
    },
    close() {
      channel.close();
    },
  };
}
